import React, { useEffect, useState } from 'react';
import { Flame, Target, Droplet, Loader2 } from 'lucide-react';
import { Card, Stat, Empty } from './shared';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const PHASES = [
  { id: 'cut',       label: 'Cut',       desc: 'Lose fat, keep strength' },
  { id: 'maintain',  label: 'Maintain',  desc: 'Hold and refine' },
  { id: 'bulk',      label: 'Bulk',      desc: 'Add size and strength' },
  { id: 'transform', label: 'Transform', desc: '5-stage long-haul journey' },
];

const PhaseSwitcher = ({ current, onChange, switching }) => (
  <div className="grid grid-cols-4 gap-1.5">
    {PHASES.map((p) => {
      const isActive = current === p.id;
      return (
        <button
          key={p.id}
          onClick={() => !isActive && onChange(p.id)}
          disabled={switching}
          data-testid={`phase-${p.id}`}
          className={`flex flex-col items-center gap-0.5 py-2 px-1 rounded-lg border text-[10px] font-black uppercase tracking-wider transition-all ${
            isActive
              ? 'bg-[#BEFF38] text-black border-[#BEFF38]'
              : 'bg-zinc-900/60 text-zinc-300 border-zinc-800 hover:border-zinc-600 disabled:opacity-50'
          }`}
        >
          {p.label}
        </button>
      );
    })}
  </div>
);

const TransformStages = ({ stages, current, onChange, switching }) => (
  <div className="mt-3 rounded-lg border border-[#2a2f3a] bg-[#0b0c10] p-3">
    <p className="text-[10px] uppercase tracking-wider font-bold text-[#BEFF38] mb-2">Transform — 5-stage journey</p>
    <ol className="space-y-1.5">
      {stages.map((s, i) => {
        const isActive = current === i;
        return (
          <li key={s.id}>
            <button
              onClick={() => !isActive && onChange(i)}
              disabled={switching}
              data-testid={`transform-stage-${i}`}
              className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-md border transition-colors ${
                isActive
                  ? 'border-[#BEFF38] bg-[#BEFF38]/10'
                  : 'border-[#1f2937] hover:border-[#2a2f3a] disabled:opacity-50'
              }`}
            >
              <span className={`shrink-0 w-6 h-6 rounded-full grid place-items-center text-[10px] font-black ${
                isActive ? 'bg-[#BEFF38] text-black' : 'bg-zinc-800 text-zinc-400'
              }`}>{i + 1}</span>
              <span className="min-w-0 flex-1">
                <span className={`block text-sm font-bold truncate ${isActive ? 'text-white' : 'text-zinc-300'}`}>{s.label}</span>
                <span className="block text-[10px] text-zinc-500">{s.weeks} weeks · {s.adjust > 0 ? `+${s.adjust}` : s.adjust} kcal</span>
              </span>
              {isActive && <span className="text-[9px] font-black uppercase tracking-wider text-[#BEFF38]">Active</span>}
            </button>
          </li>
        );
      })}
    </ol>
  </div>
);

const TargetsCard = ({ refreshKey }) => {
  const [targets, setTargets] = useState(null);
  const [plan, setPlan] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    api.planTypes()
      .then((d) => setStages(d?.transform_stages || []))
      .catch(() => setStages([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([api.targets().catch(() => null), api.getPlan().catch(() => null)])
      .then(([t, p]) => {
        if (t) { setTargets(t); setErr(null); } else { setErr('No plan yet'); setTargets(null); }
        setPlan(p);
      })
      .finally(() => setLoading(false));
  }, [refreshKey, tick]);

  const switchPhase = async (newType) => {
    if (!plan) {
      toast({ title: 'Set up your plan first', variant: 'destructive' });
      return;
    }
    setSwitching(true);
    try {
      await api.upsertPlan({ ...plan, plan_type: newType, transform_stage: 0 });
      toast({ title: 'Phase switched', description: PHASES.find((p) => p.id === newType)?.label });
      setTick((n) => n + 1);
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSwitching(false); }
  };

  const switchStage = async (stageIdx) => {
    if (!plan) return;
    setSwitching(true);
    try {
      await api.upsertPlan({ ...plan, plan_type: 'transform', transform_stage: stageIdx });
      toast({ title: `Stage ${stageIdx + 1} active`, description: stages[stageIdx]?.label });
      setTick((n) => n + 1);
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSwitching(false); }
  };

  return (
    <Card title="Today's targets" icon={Target}>
      {loading ? (
        <div className="flex items-center gap-2 text-[#aab1bf] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</div>
      ) : err || !targets ? (
        <Empty>Save your plan to see daily calories and macros.</Empty>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] font-black text-[#BEFF38] mb-2">Phase</p>
            <PhaseSwitcher current={plan?.plan_type} onChange={switchPhase} switching={switching} />
            <p className="text-[11px] text-zinc-500 mt-1.5">
              {PHASES.find((p) => p.id === plan?.plan_type)?.desc || targets.phase_label}
            </p>
            {plan?.plan_type === 'transform' && stages.length > 0 && (
              <TransformStages
                stages={stages}
                current={plan?.transform_stage || 0}
                onChange={switchStage}
                switching={switching}
              />
            )}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <Stat label="Calories" value={`${targets.calories} kcal`} sub={<span><Flame className="inline w-3 h-3" /> daily target</span>} />
            <Stat label="Protein" value={`${targets.protein_g} g`} sub={`${Math.round(targets.protein_g * 4)} kcal`} />
            <Stat label="Carbs"   value={`${targets.carbs_g} g`}   sub={`${Math.round(targets.carbs_g * 4)} kcal`} />
            <Stat label="Fats"    value={`${targets.fats_g} g`}    sub={`${Math.round(targets.fats_g * 9)} kcal`} />
            <Stat label="Fiber"   value={`${targets.fiber_g} g`} />
            <Stat label="Hydration" value={`${targets.hydration_ml} ml`} sub={<span><Droplet className="inline w-3 h-3" /> water</span>} />
          </div>
        </div>
      )}
    </Card>
  );
};

export default TargetsCard;
